import { useEffect, useState } from "react";
import { Ambulance } from "lucide-react";

// =============================================================================
// BookingInProgress — full-screen "please wait, booking in progress" overlay.
//
// An ambulance sits inside a spinning brand ring with a soft glow. It renders
// only when `active`, and the parent gates `active` through useDelayedFlag, so
// a normal (instant) booking never sees it — only a genuinely delayed one.
// After ~6s the subtext softens to acknowledge the longer wait. Honors
// prefers-reduced-motion (the ring + entrance stop animating; the message and
// aria-busy still convey progress).
// =============================================================================

const LONG_WAIT_MS = 6000;

export function BookingInProgress({ active }: { active: boolean }) {
  const [longWait, setLongWait] = useState(false);

  useEffect(() => {
    if (!active) {
      setLongWait(false);
      return;
    }
    const timer = setTimeout(() => setLongWait(true), LONG_WAIT_MS);
    return () => clearTimeout(timer);
  }, [active]);

  if (!active) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      className="fixed inset-0 z-[60] flex flex-col items-center justify-center gap-5 bg-background/85 backdrop-blur-sm motion-safe:animate-in motion-safe:fade-in"
    >
      <div className="relative h-28 w-28 drop-shadow-[0_0_22px_hsl(var(--primary)/0.45)]">
        <svg
          viewBox="0 0 100 100"
          className="h-full w-full motion-safe:animate-[spin_1.1s_linear_infinite]"
          aria-hidden="true"
        >
          <circle
            cx="50"
            cy="50"
            r="44"
            fill="none"
            className="stroke-foreground/10"
            strokeWidth="6"
          />
          <circle
            cx="50"
            cy="50"
            r="44"
            fill="none"
            className="stroke-primary"
            strokeWidth="6"
            strokeLinecap="round"
            strokeDasharray="90 220"
          />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-primary">
          <Ambulance className="h-10 w-10" aria-hidden="true" />
        </span>
      </div>
      <div className="px-6 text-center">
        <p className="text-base font-bold text-foreground">Booking your trip…</p>
        <p className="mt-1 text-sm text-muted-foreground">
          {longWait
            ? "Still working — hang tight."
            : "Dispatching to Royal Transportation"}
        </p>
      </div>
    </div>
  );
}
